import { RefObject, useCallback, useEffect, useRef, useState } from "react"
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list"
import { $getSelectionStyleValueForProperty, $patchStyleText } from "@lexical/selection"
import { INSERT_TABLE_COMMAND } from "@lexical/table"
import { Button, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import {
  $getSelection,
  $isRangeSelection,
  $setSelection,
  BaseSelection,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  LexicalEditor,
  OUTDENT_CONTENT_COMMAND,
} from "lexical"
import {
  BoldIcon,
  ChevronDownIcon,
  IndentDecreaseIcon,
  IndentIncreaseIcon,
  ItalicIcon,
  Link2,
  ListIcon,
  ListOrderedIcon,
  ListTodoIcon,
  PaintbrushVerticalIcon,
  StrikethroughIcon,
  TableIcon,
  UnderlineIcon,
} from "lucide-react"

import { COLOR_PRESETS } from "@/components/lexical/color-presets"
import { OPEN_FLOATING_LINK_EDITOR_COMMAND } from "@/components/lexical/plugins/floating-link-editor"

const FONT_FAMILIES = [
  { label: "Default", value: "default" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Aptos", value: "Aptos, Aptos_EmbeddedFont, Aptos_MSFontService, Calibri, Helvetica, sans-serif" },
  { label: "Calibri", value: "Calibri, Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: '"Times New Roman", Times, serif' },
  { label: "Verdana", value: "Verdana, Arial, sans-serif" },
] as const

const FONT_SIZES = [
  { label: "Default", value: "default" },
  { label: "10", value: "10pt" },
  { label: "12", value: "12pt" },
  { label: "14", value: "14pt" },
  { label: "18", value: "18pt" },
  { label: "24", value: "24pt" },
] as const

interface EditorRef {
  getEditor: () => LexicalEditor | null
}

export function EditorFormatButtons({
  editorRef,
  enableTable = false,
  enableChecklist = false,
  wrap = false,
}: {
  editorRef: RefObject<EditorRef | null>
  enableTable?: boolean
  enableChecklist?: boolean
  wrap?: boolean
}) {
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [currentColor, setCurrentColor] = useState("")
  const [currentFontFamily, setCurrentFontFamily] = useState("")
  const [currentFontSize, setCurrentFontSize] = useState("")
  const [fontFamilyOpen, setFontFamilyOpen] = useState(false)
  const [fontSizeOpen, setFontSizeOpen] = useState(false)
  const savedSelectionRef = useRef<BaseSelection | null>(null)
  const currentFontFamilyLabel = FONT_FAMILIES.find((font) => font.value === currentFontFamily)?.label ?? "Default"
  const currentFontSizeLabel = FONT_SIZES.find((size) => size.value === currentFontSize)?.label ?? "Default"

  const updateFormatState = useCallback(() => {
    const editor = editorRef.current?.getEditor()
    if (!editor) return
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        setIsBold(selection.hasFormat("bold"))
        setIsItalic(selection.hasFormat("italic"))
        setIsUnderline(selection.hasFormat("underline"))
        setIsStrikethrough(selection.hasFormat("strikethrough"))
        setCurrentColor($getSelectionStyleValueForProperty(selection, "color", ""))
        setCurrentFontFamily($getSelectionStyleValueForProperty(selection, "font-family", ""))
        setCurrentFontSize($getSelectionStyleValueForProperty(selection, "font-size", ""))
      }
    })
  }, [editorRef])

  useEffect(() => {
    document.addEventListener("selectionchange", updateFormatState)
    return () => document.removeEventListener("selectionchange", updateFormatState)
  }, [updateFormatState])

  useEffect(() => {
    // The editor initializes asynchronously, so poll briefly until it's available.
    let cleanup: (() => void) | undefined
    const tryRegister = () => {
      const editor = editorRef.current?.getEditor()
      if (!editor) return false
      cleanup = editor.registerUpdateListener(() => updateFormatState())
      return true
    }
    if (tryRegister()) return () => cleanup?.()

    const id = setInterval(() => {
      if (tryRegister()) clearInterval(id)
    }, 100)
    return () => {
      clearInterval(id)
      cleanup?.()
    }
  }, [editorRef, updateFormatState])

  const dispatchCommand = (command: Parameters<LexicalEditor["dispatchCommand"]>[0], payload: unknown) => {
    const editor = editorRef.current?.getEditor()
    if (!editor) return
    editor.focus()
    editor.dispatchCommand(command, payload)
  }

  const saveSelection = () => {
    const editor = editorRef.current?.getEditor()
    if (!editor) return
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        savedSelectionRef.current = selection.clone()
      }
    })
  }

  const saveSelectionForKeyboardOpen = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") saveSelection()
  }

  const insertTable = () => {
    dispatchCommand(INSERT_TABLE_COMMAND, {
      rows: "2",
      columns: "2",
      includeHeaders: { rows: true, columns: false },
    })
  }

  const applyColor = (color: string) => {
    applyStyle("color", color)
  }

  const applyStyle = (property: "color" | "font-family" | "font-size", value: string) => {
    const editor = editorRef.current?.getEditor()
    if (!editor || !savedSelectionRef.current) return
    editor.focus()
    editor.update(() => {
      $setSelection(savedSelectionRef.current!.clone())
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { [property]: value === "default" ? "" : value })
      }
    })
  }

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className={`flex max-w-full min-w-0 items-center gap-0.5 ${wrap ? "flex-wrap" : "overflow-x-auto"}`}
    >
      <Popover open={fontFamilyOpen} onOpenChange={setFontFamilyOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-40 shrink-0 justify-between overflow-hidden px-2 text-xs font-normal"
            aria-label="Font family"
            onPointerDown={saveSelection}
            onKeyDown={saveSelectionForKeyboardOpen}
          >
            <span className="min-w-0 flex-1 truncate text-left">{currentFontFamilyLabel}</span>
            <ChevronDownIcon className="size-3.5!" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-1" side="top" align="start">
          {FONT_FAMILIES.map((font) => (
            <button
              key={font.value}
              type="button"
              className={`hover:bg-tertiary flex w-full rounded px-2 py-1.5 text-left text-sm ${
                currentFontFamilyLabel === font.label ? "bg-blue-background" : ""
              }`}
              style={font.value === "default" ? undefined : { fontFamily: font.value }}
              onClick={() => {
                applyStyle("font-family", font.value)
                setFontFamilyOpen(false)
              }}
            >
              {font.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Popover open={fontSizeOpen} onOpenChange={setFontSizeOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 min-w-24 shrink-0 justify-between px-2 text-xs font-normal"
            aria-label="Font size"
            onPointerDown={saveSelection}
            onKeyDown={saveSelectionForKeyboardOpen}
          >
            <span className="min-w-0 flex-1 truncate text-left">{currentFontSizeLabel}</span>
            <ChevronDownIcon className="size-3.5!" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-24 p-1" side="top" align="start">
          {FONT_SIZES.map((size) => (
            <button
              key={size.value}
              type="button"
              className={`hover:bg-tertiary flex w-full rounded px-2 py-1.5 text-left text-sm ${
                currentFontSizeLabel === size.label ? "bg-blue-background" : ""
              }`}
              onClick={() => {
                applyStyle("font-size", size.value)
                setFontSizeOpen(false)
              }}
            >
              {size.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Button
        size="iconSm"
        variant={isBold ? "secondary" : "ghost"}
        type="button"
        onClick={() => dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        tooltip="Bold"
      >
        <BoldIcon className="text-icon size-3.5!" />
      </Button>
      <Button
        size="iconSm"
        variant={isItalic ? "secondary" : "ghost"}
        type="button"
        onClick={() => dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        tooltip="Italic"
      >
        <ItalicIcon className="text-icon size-3.5!" />
      </Button>
      <Button
        size="iconSm"
        variant={isUnderline ? "secondary" : "ghost"}
        type="button"
        onClick={() => dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
        tooltip="Underline"
      >
        <UnderlineIcon className="text-icon size-3.5!" />
      </Button>
      <Button
        size="iconSm"
        variant={isStrikethrough ? "secondary" : "ghost"}
        type="button"
        onClick={() => dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")}
        tooltip="Strikethrough"
      >
        <StrikethroughIcon className="text-icon size-3.5!" />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            size="iconSm"
            variant={currentColor ? "secondary" : "ghost"}
            type="button"
            tooltip="Text color"
            onPointerDown={saveSelection}
            onKeyDown={saveSelectionForKeyboardOpen}
          >
            <PaintbrushVerticalIcon className="text-icon" style={currentColor ? { color: currentColor } : undefined} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" side="top" align="start">
          <div className="flex flex-col gap-1">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={`hover:bg-tertiary flex items-center gap-2 rounded px-2 py-1 text-sm ${
                  (preset.value === "" && !currentColor) || preset.value === currentColor ? "bg-blue-background" : ""
                }`}
                onClick={() => applyColor(preset.value)}
              >
                <span
                  className="size-4 shrink-0 rounded-full"
                  style={{ backgroundColor: preset.value || "var(--foreground)" }}
                />
                <span className="text-foreground">{preset.label}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <Button
        size="iconSm"
        variant="ghost"
        type="button"
        onClick={() => dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        tooltip="Bullet list"
      >
        <ListIcon className="text-icon size-3.5!" />
      </Button>
      <Button
        size="iconSm"
        variant="ghost"
        type="button"
        onClick={() => dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        tooltip="Numbered list"
      >
        <ListOrderedIcon className="text-icon size-3.5!" />
      </Button>
      {enableChecklist && (
        <Button
          size="iconSm"
          variant="ghost"
          type="button"
          onClick={() => dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)}
          tooltip="Checklist"
        >
          <ListTodoIcon className="text-icon size-3.5!" />
        </Button>
      )}
      <Button
        size="iconSm"
        variant="ghost"
        type="button"
        onClick={() => dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
        tooltip="Decrease indent"
      >
        <IndentDecreaseIcon className="text-icon size-3.5!" />
      </Button>
      <Button
        size="iconSm"
        variant="ghost"
        type="button"
        onClick={() => dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
        tooltip="Increase indent"
      >
        <IndentIncreaseIcon className="text-icon size-3.5!" />
      </Button>
      {enableTable && (
        <Button size="iconSm" variant="ghost" type="button" tooltip="Insert table" onClick={insertTable}>
          <TableIcon className="text-icon size-3.5!" />
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        aria-label="Insert link"
        tooltip="Insert link"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editorRef.current?.getEditor()?.dispatchCommand(OPEN_FLOATING_LINK_EDITOR_COMMAND, undefined)}
      >
        <Link2 />
      </Button>
    </div>
  )
}
